import React, { useState } from "react";
import {
  Box,
  CssBaseline,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Fade,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";

import DashboardIcon from "@mui/icons-material/Dashboard";
import BuildIcon from "@mui/icons-material/Build";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventNoteIcon from "@mui/icons-material/EventNote";
import EngineeringIcon from "@mui/icons-material/Engineering";

import { Link, Outlet, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { productsActions, sessionActions, usersActions } from "../store/";

const drawerWidth = 260;

/* -------------------- DRAWER STYLES -------------------- */
const openedMixin = (theme) => ({
  width: drawerWidth,
  background: "rgba(12, 32, 61, 1)",
  transition: theme.transitions.create("width", {
    duration: 300,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  width: `calc(${theme.spacing(7)} + 1px)`,
  transition: theme.transitions.create("width", {
    duration: 260,
  }),
  overflowX: "hidden",
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: "linear-gradient(90deg, #002355 0%, #0082fb 100%)",
  transition: theme.transitions.create(["width", "margin"], {
    duration: 300,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    ...(open ? openedMixin(theme) : closedMixin(theme)),
    color: "#fff",
  },
}));

/* -------------------- COMPONENT -------------------- */
export default function MiniDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const role = useSelector((state) => state.session.user?.role);
  const username = useSelector(
    (state) => state.session.user?.email || "Technicien"
  );

  const toggleDrawer = () => setOpen(!open);

  const logout = () => {
    localStorage.removeItem("TOKEN");
    navigate("/login");
    dispatch(sessionActions.updateUser({ user: {} }));
    dispatch(productsActions.setItems([]));
    dispatch(usersActions.setItems([]));
  };

  /* -------------------- MENU -------------------- */
  const menuItems = [
    { name: "Dashboard", icon: <DashboardIcon />, link: "/dashboard" },
    { name: "Maintenances", icon: <BuildIcon />, link: "/settings/maintenances" },
    { name: "Stats", icon: <AssessmentIcon />, link: "/settings/stats" },
  ];

  if (role === "superadmin" || role === "admin") {
    menuItems.push(
      { name: "Produits", icon: <Inventory2Icon />, link: "/settings/products" },
      { name: "Clients", icon: <PeopleIcon />, link: "/settings/clients" },
            { name: "Fournisseurs", icon: <PeopleIcon />, link: "/settings/suppliers" },

      { name: "Livraisons", icon: <LocalShippingIcon />, link: "/settings/shippings" },
      { name: "Simulation Présence", icon: <EventAvailableIcon />, link: "/settings/presence" },
      { name: "Rapport Presence", icon: <EventNoteIcon />, link: "/settings/presenceReports" },
       
      { name: "Utilisateurs", icon: <PersonIcon />, link: "/settings/users" }
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* APPBAR */}
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton color="inherit" onClick={toggleDrawer}>
            <MenuIcon />
          </IconButton>

          <Typography sx={{ fontWeight: 600, ml: 1 }}>
            🚍 Plateforme Maintenance Transport
          </Typography>

          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Typography>{username}</Typography>
            <Tooltip title="Logout">
              <IconButton onClick={logout} sx={{ color: "white" }}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* DRAWER */}
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <Fade in={open}>
            <Typography fontWeight={600}>Maintenance</Typography>
          </Fade>

          <IconButton onClick={toggleDrawer} sx={{ color: "white" }}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>

        <Divider />

        <List>
          {menuItems.map((item) => (
            <ListItem key={item.name} disablePadding component={Link} to={item.link}>
              <ListItemButton
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  color: "#fff",
                }}
              >
                <ListItemIcon sx={{ color: "inherit" }}>
                  {item.icon}
                </ListItemIcon>

                <ListItemText primary={item.name} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* CONTENT */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
}